import { useState, useCallback } from "react";
import {
  Modal,
  BlockStack,
  InlineStack,
  TextField,
  Select,
  Button,
  FormLayout,
  Text,
  Divider,
  Autocomplete,
  Icon,
  Tag,
  Box,
  Combobox,
  Listbox,
  Bleed,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import { SearchIcon, XIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { showToast } from "@shared/lib";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { AgendaSession, MultilingualText, SessionTag, Instructor } from "@features/events/types";
import { GenerativeTextField } from "@shared/ui";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import { getLocalizedText } from "@shared/lib";

interface AddEditSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    session: AgendaSession,
    placement?: { dayIndex: number; swimlaneIndex: number }
  ) => void;
  session?: AgendaSession;
  isEditMode?: boolean;
  eventInstructorIds?: string[]; // Add this prop to receive event's assigned instructor IDs
  targetDayIndex?: number;
  targetSwimlaneIndex?: number;
  dayOptions?: { label: string; value: string }[];
  trackOptions?: { label: string; value: string }[];
  showDaySelector?: boolean;
  showTrackSelector?: boolean;
  onTargetDayChange: (value: string) => void;
  onTargetSwimlaneChange: (value: string) => void;
}

// Predefined session tags with colors
const PREDEFINED_TAGS: SessionTag[] = [
  {
    label: { bg: "Почивка", en: "Break" },
    color: "secondary",
  },
  {
    label: { bg: "Кафе пауза", en: "Coffee break" },
    color: "secondary",
  },
  {
    label: { bg: "Обедна пауза", en: "Lunch break" },
    color: "secondary",
  },
  {
    label: { bg: "Уъркшоп", en: "Workshop" },
    color: "warning",
  },
  {
    label: { bg: "Лекция", en: "Lecture" },
    color: "info",
  },
  {
    label: { bg: "Презентация", en: "Presentation" },
    color: "info",
  },
  {
    label: { bg: "Дискусия", en: "Discussion" },
    color: "success",
  },
  {
    label: { bg: "Практическо упражнение", en: "Hands-on practice" },
    color: "warning",
  },
];

const DraggableInstructor = ({
  instructor,
  onRemove,
}: {
  instructor: Instructor;
  onRemove: (instructorId: string) => void;
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <SortableResourceItem
      accessibilityLabel={`View details for ${instructor.nameWithPrefix || "instructor"}`}
      dragHandleAccessibilityLabel={`Reorder ${instructor.nameWithPrefix || "instructor"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <EventsDefaultAvatar
              name={instructor.nameWithPrefix || ""}
              singleImage={instructor.avatar}
            />
            <BlockStack gap="100">
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {instructor.nameWithPrefix}
              </Text>
              <Box maxWidth="300px">
                <Text as="span" variant="bodySm" tone="subdued" truncate>
                  {getLocalizedText(instructor.geoAddress?.summary)}
                </Text>
              </Box>
            </BlockStack>
          </InlineStack>
          <div style={{ opacity: isHovered ? 1 : 0, display: "flex" }}>
            <Button
              icon={XIcon}
              tone="critical"
              variant="tertiary"
              onClick={() => onRemove(instructor.id)}
              accessibilityLabel={t(
                "eventDetails.agenda.session.removeInstructor"
              )}
            />
          </div>
        </InlineStack>
      </div>
    </SortableResourceItem>
  );
};

export default function AddEditSessionModal({
  isOpen,
  onClose,
  onSave,
  session,
  isEditMode = false,
  eventInstructorIds,
  targetDayIndex,
  targetSwimlaneIndex,
  dayOptions = [],
  trackOptions = [],
  showDaySelector = false,
  showTrackSelector = false,
  onTargetDayChange,
  onTargetSwimlaneChange,
}: AddEditSessionModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { GET } = useApiRequest();

  // Form state
  const [fromTime, setFromTime] = useState(() => session?.from || "");
  const [toTime, setToTime] = useState(() => session?.to || "");
  const [title, setTitle] = useState<MultilingualText>(() => session?.title ?? {});
  const [description, setDescription] = useState<MultilingualText>(
    () => session?.description ?? {}
  );
  const [selectedInstructorIds, setSelectedInstructorIds] = useState<string[]>(
    () => session?.instructorIds ?? []
  );
  const [selectedTags, setSelectedTags] = useState<SessionTag[]>(
    () => session?.tags ?? []
  );

  // Tag search state
  const [tagQuery, setTagQuery] = useState("");
  const [availableTags] = useState(PREDEFINED_TAGS);

  // Instructor search state
  const [instructorQuery, setInstructorQuery] = useState("");

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isDescriptionValid, setIsDescriptionValid] = useState(true);

  // Fetch instructors for display
  const { data: allInstructors = [] } = useQuery<Instructor[]>({
    queryKey: ["all-instructors", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/instructors`),
    enabled: Boolean(hcc && isOpen),
  });

  // Get assigned instructors
  const assignedInstructors = selectedInstructorIds
    .map((id) => allInstructors.find((instructor) => instructor.id === id))
    .filter(Boolean) as Instructor[];

  // Convert time string to minutes for comparison
  const timeToMinutes = (timeStr: string): number => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  };

  // Validate time format (HH:MM)
  const isValidTimeFormat = (timeStr: string): boolean => {
    if (!timeStr.includes(":")) return false;
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  };

  // Validation
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};
    const hasFromTime = Boolean(fromTime.trim());
    const hasToTime = Boolean(toTime.trim());
    const isFromTimeValid = hasFromTime && isValidTimeFormat(fromTime);
    const isToTimeValid = hasToTime && isValidTimeFormat(toTime);

    if (!hasFromTime) {
      newErrors.fromTime = t(
        "eventDetails.agenda.session.validation.fromTimeRequired"
      );
    }
    if (!hasToTime) {
      newErrors.toTime = t(
        "eventDetails.agenda.session.validation.toTimeRequired"
      );
    }

    if (hasFromTime && !isFromTimeValid) {
      newErrors.fromTime = t(
        "eventDetails.agenda.session.validation.invalidTimeFormat"
      );
    }

    if (hasToTime && !isToTimeValid) {
      newErrors.toTime = t(
        "eventDetails.agenda.session.validation.invalidTimeFormat"
      );
    }

    // Time order validation
    if (isFromTimeValid && isToTimeValid) {
      const fromMinutes = timeToMinutes(fromTime);
      const toMinutes = timeToMinutes(toTime);
      if (fromMinutes >= toMinutes) {
        newErrors.toTime = t(
          "eventDetails.agenda.session.validation.endTimeAfterStart"
        );
      }
    }

    setErrors(newErrors);
    // Return true only if there are no errors AND all language validations pass
    return (
      Object.keys(newErrors).length === 0 &&
      isTitleValid &&
      isDescriptionValid
    );
  }, [fromTime, toTime, t, isTitleValid, isDescriptionValid]);

  // Format time with leading zeros
  const formatTimeWithLeadingZeros = (timeStr: string): string => {
    if (!timeStr.includes(":")) return timeStr;

    const [hours, minutes] = timeStr.split(":");
    const formattedHours = hours.padStart(2, "0");
    const formattedMinutes = minutes.padStart(2, "0");

    return `${formattedHours}:${formattedMinutes}`;
  };

  // Validate time fields on blur
  const validateTimeField = (timeValue: string, fieldName: string) => {
    const newErrors = { ...errors };

    if (timeValue.trim()) {
      if (!isValidTimeFormat(timeValue)) {
        newErrors[fieldName] = t(
          "eventDetails.agenda.session.validation.invalidTimeFormat"
        );
      } else {
        // Format time with leading zeros if valid
        const formattedTime = formatTimeWithLeadingZeros(timeValue);
        if (formattedTime !== timeValue) {
          if (fieldName === "fromTime") {
            setFromTime(formattedTime);
          } else {
            setToTime(formattedTime);
          }
        }
        delete newErrors[fieldName];
      }
    }

    // Check if end time is after start time when both are present
    if (fromTime && toTime && fieldName === "toTime") {
      const fromMinutes = timeToMinutes(fromTime);
      const toMinutes = timeToMinutes(toTime);
      if (fromMinutes >= toMinutes) {
        newErrors.toTime = t(
          "eventDetails.agenda.session.validation.endTimeAfterStart"
        );
      }
    }

    setErrors(newErrors);
  };

  // Format time input
  const formatTimeInput = (value: string): string => {
    // Remove non-numeric characters except colon
    const cleaned = value.replace(/[^\d:]/g, "");

    // Auto-format HH:MM

    if (cleaned.length > 5) {
      return cleaned.substring(0, 5);
    }
    return cleaned;
  };

  const handleFromTimeChange = (value: string) => {
    const formattedValue = formatTimeInput(value);
    setFromTime(formattedValue);
    // Clear fromTime error when user starts typing
    if (errors.fromTime && formattedValue.trim()) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.fromTime;
        return newErrors;
      });
    }
  };

  const handleToTimeChange = (value: string) => {
    const formattedValue = formatTimeInput(value);
    setToTime(formattedValue);
    // Clear toTime error when user starts typing
    if (errors.toTime && formattedValue.trim()) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.toTime;
        return newErrors;
      });
    }
  };

  // Handle tag selection
  const handleTagSelect = useCallback(
    (selectedOptions: string[]) => {
      if (selectedOptions.length > 0) {
        const tagLabel = selectedOptions[0];
        const tag = availableTags.find(
          (t) => t.label.bg === tagLabel || t.label.en === tagLabel
        );

        if (
          tag &&
          !selectedTags.find(
            (t) => t.label.bg === tag.label.bg && t.label.en === tag.label.en
          )
        ) {
          setSelectedTags([...selectedTags, tag]);
        }
        setTagQuery("");
      }
    },
    [availableTags, selectedTags]
  );

  const handleTagRemove = useCallback(
    (index: number) => {
      setSelectedTags(selectedTags.filter((_, i) => i !== index));
    },
    [selectedTags]
  );

  // Filter tags based on search query
  const filteredTags = availableTags.filter((tag) => {
    const query = tagQuery.toLowerCase();
    return (
      tag.label.bg.toLowerCase().includes(query) ||
      tag.label.en.toLowerCase().includes(query)
    );
  });

  const handleRemoveInstructor = (instructorId: string) => {
    setSelectedInstructorIds((prev) =>
      prev.filter((id) => id !== instructorId)
    );
  };

  // Prepare instructor options for Combobox - filter to only event instructors
  const filteredInstructors = allInstructors
    .filter((instructor) => eventInstructorIds?.includes(instructor.id))
    .filter((instructor) =>
      (instructor.nameWithPrefix || "")
        .toLowerCase()
        .includes(instructorQuery.toLowerCase())
    );

  // Handle instructor selection from Combobox
  const handleInstructorSelect = (instructorId: string) => {
    if (!selectedInstructorIds.includes(instructorId)) {
      setSelectedInstructorIds([...selectedInstructorIds, instructorId]);
    }
    setInstructorQuery("");
  };

  // Handle save
  const handleSave = useCallback(() => {
    if (!validateForm()) {
      // Show toast error when validation fails
      dispatch(
        showToast({
          message: t("eventDetails.agenda.session.validation.error_missing_fields", {
            defaultValue: "Please fill in all required fields in both languages.",
          }),
          type: "error",
        })
      );
      return;
    }

    const sessionData: AgendaSession = {
      multiLine: false,
      from: fromTime,
      to: toTime,
      title: title,
      description: description,
      instructorIds:
        selectedInstructorIds.length > 0 ? selectedInstructorIds : undefined,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
    };

    const placement =
      isEditMode &&
      typeof targetDayIndex === "number" &&
      typeof targetSwimlaneIndex === "number"
        ? { dayIndex: targetDayIndex, swimlaneIndex: targetSwimlaneIndex }
        : undefined;

    onSave(sessionData, placement);
    onClose();
  }, [
    validateForm,
    fromTime,
    toTime,
    title,
    description,
    selectedInstructorIds,
    selectedTags,
    isEditMode,
    targetDayIndex,
    targetSwimlaneIndex,
    onSave,
    onClose,
    dispatch,
    t,
  ]);

  const handleCancel = () => {
    onClose();
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleCancel}
        title={
          isEditMode
            ? t("eventDetails.agenda.session.editTitle")
            : t("eventDetails.agenda.session.addTitle")
        }
        size="large"
        primaryAction={{
          content: isEditMode
            ? t("common.update")
            : t("eventDetails.agenda.session.addButton"),
          onAction: handleSave,
        }}
        secondaryActions={[
          {
            content: t("common.cancel"),
            onAction: handleCancel,
          },
        ]}
        onTransitionEnd={() => {
          document.body.setAttribute("data-lock-scrolling-hidden", "true");
        }}
      >
        <Modal.Section>
          <FormLayout>
            {/* Time Section */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "var(--p-space-400)",
              }}
            >
              <TextField
                label={t("eventDetails.agenda.session.startTime")}
                value={fromTime}
                onChange={handleFromTimeChange}
                onBlur={() => validateTimeField(fromTime, "fromTime")}
                placeholder={t("eventDetails.agenda.session.startTimePlaceholder")}
                error={errors.fromTime}
                autoComplete="off"
                requiredIndicator
              />
              <TextField
                label={t("eventDetails.agenda.session.endTime")}
                value={toTime}
                onChange={handleToTimeChange}
                onBlur={() => validateTimeField(toTime, "toTime")}
                placeholder={t("eventDetails.agenda.session.endTimePlaceholder")}
                error={errors.toTime}
                autoComplete="off"
                requiredIndicator
              />

              {showDaySelector && (
                <Select
                  label={t("eventDetails.agenda.session.day")}
                  options={dayOptions}
                  value={String(targetDayIndex ?? 0)}
                  onChange={onTargetDayChange}
                />
              )}

              {showTrackSelector && trackOptions.length > 0 && (
                <Select
                  label={t("eventDetails.agenda.session.track")}
                  options={trackOptions}
                  value={String(targetSwimlaneIndex ?? 0)}
                  onChange={onTargetSwimlaneChange}
                />
              )}
            </div>

            <Bleed marginInline="400">
              <Divider />
            </Bleed>

            {/* Session Title */}
            <BlockStack gap="200">
              <GenerativeTextField
                id="session-title-bg"
                placementId="event.event.session.title"
                label={t("eventDetails.agenda.session.title")}
                value={title}
                onChange={(value) => setTitle(value)}
                requiredIndicator
                validateAllLanguages={true}
                onValidationChange={setIsTitleValid}
              />
            </BlockStack>

            {/* Session Description */}
            <BlockStack gap="200">
              <GenerativeTextField
                id="session-description-bg"
                placementId="event.event.session.description"
                label={t("eventDetails.agenda.session.description")}
                value={description}
                onChange={(value) => setDescription(value)}
                type="textArea"
                canGenerate={true}
                validateAllLanguages={true}
                onValidationChange={setIsDescriptionValid}
              />
            </BlockStack>

            <Bleed marginInline="400">
              <Divider />
            </Bleed>

            {/* Instructors */}
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">
                {t("eventDetails.agenda.session.instructors")}
              </Text>

              <Combobox
                allowMultiple
                activator={
                  <Combobox.TextField
                    prefix={<Icon source={SearchIcon} />}
                    onChange={setInstructorQuery}
                    label={t("eventDetails.agenda.session.selectInstructors")}
                    placeholder={t(
                      "eventDetails.agenda.session.selectInstructors"
                    )}
                    labelHidden
                    value={instructorQuery}
                    autoComplete="off"
                  />
                }
              >
                {filteredInstructors.length > 0 ? (
                  <Listbox onSelect={handleInstructorSelect}>
                    {filteredInstructors.map((instructor) => (
                      <Listbox.Option
                        key={instructor.id}
                        value={instructor.id}
                        selected={selectedInstructorIds.includes(instructor.id)}
                      >
                        {instructor.nameWithPrefix}
                      </Listbox.Option>
                    ))}
                  </Listbox>
                ) : null}
              </Combobox>

              {assignedInstructors.length > 0 && (
                <Box
                  borderWidth="025"
                  borderColor="border"
                  borderRadius="200"
                  padding="0"
                >
                    <SortableList
                      items={assignedInstructors}
                      idForItem={(instructor) => instructor.id}
                      onChange={(updated) =>
                        setSelectedInstructorIds(updated.map((entry) => entry.id))
                      }
                      renderItem={(instructor) => (
                        <DraggableInstructor
                          instructor={instructor}
                          onRemove={handleRemoveInstructor}
                        />
                      )}
                    />
                  </Box>
                )}
            </BlockStack>

            <Bleed marginInline="400">
              <Divider />
            </Bleed>

            {/* Tags */}
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">
                {t("eventDetails.agenda.session.tags")}
              </Text>

              <Autocomplete
                options={filteredTags.map((tag) => {
                  const localizedLabel = getLocalizedText(tag.label);
                  return {
                    value: localizedLabel,
                    label: localizedLabel,
                  };
                })}
                selected={[]}
                onSelect={handleTagSelect}
                textField={
                  <Autocomplete.TextField
                    prefix={<Icon source={SearchIcon} />}
                    label=""
                    onChange={setTagQuery}
                    value={tagQuery}
                    placeholder={t("eventDetails.agenda.session.searchTags")}
                    clearButton
                    onClearButtonClick={() => setTagQuery("")}
                    autoComplete="off"
                  />
                }
              />

              {selectedTags.length > 0 && (
                <InlineStack gap="200">
                  {selectedTags.map((tag, index) => (
                    <Tag key={index} onRemove={() => handleTagRemove(index)}>
                      {getLocalizedText(tag.label)}
                    </Tag>
                  ))}
                </InlineStack>
              )}

              {selectedTags.length === 0 && (
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t("eventDetails.agenda.session.noTagsSelected")}
                </Text>
              )}
            </BlockStack>
          </FormLayout>
        </Modal.Section>
      </Modal>
    </>
  );
}
